import { Col, Form, Input, Row, Select, AutoComplete, message, ConfigProvider, Button, Checkbox } from "antd";
import MainLayout from "../../layouts/Layout";
import styles from "./user.module.scss";
import { RevisdomAPIViewModelsUserCreateUserRequestRoleEnum } from "../../service/models/RevisdomAPIViewModelsUserCreateUserRequest";
import { useState, useEffect, useRef } from "react";
import { RevisdomAPIViewModelsOrganizationOrganizationResponse } from "../../service/models/RevisdomAPIViewModelsOrganizationOrganizationResponse";
import { useNavigate } from "react-router-dom";
import { useSecureService } from "../../store/service";
import { useAtomValue } from "jotai";
import { userAtom } from "../../store/atoms";
import { UserOutlined } from "@ant-design/icons";

interface OrganizationOption {
  value: string;
  label: string;
  id: string;
}
function getRoleValue(roleLabel: string) {
  switch (roleLabel) {
    case "Account Manager (0)": return 0;
    case "Account Owner (1)": return 1;
    case "Internal Team (2)": return 2;
    case "Brand Account Viewer (3)": return 3;
    case "Super Admin (4)": return 4;
    default: return undefined;
  }
}
const AddNewUser = () => {
  const navigate = useNavigate();
  const { getOrganizations, createUser } = useSecureService();
  const [form] = Form.useForm();
  const [organizations, setOrganizations] = useState<RevisdomAPIViewModelsOrganizationOrganizationResponse[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<{ id: string, name: string } | null>(null);
  const [sendPasswordWithEmail, setSendPasswordWithEmail] = useState(true);
  const autoCompleteRef = useRef<any>(null);
  const user = useAtomValue(userAtom);
  useEffect(() => {

    // Admin, SuperAdmin, AccountOwner kullanıcı ekleyebilir
    if (!user || !['Admin', 'SuperAdmin', 'AccountOwner'].includes(user.role)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);
  useEffect(() => {
    fetchOrganizations();
  }, []);

  const handleSessionExpired = () => {
    message.error("Session expired. Please log in again.");
    navigate('/login');
  };

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await getOrganizations();
      
      if (response.success && response.data) {
        setOrganizations(response.data);
      } else {
        message.warning("Organization list is empty.");
      }
    } catch (error) {
      handleSessionExpired();
    } finally {
      setLoading(false);
    }
  };

  const options: OrganizationOption[] = organizations
    .filter(org => 
      org.name?.toLowerCase().includes(searchValue.toLowerCase())
    )
    .map(org => ({
      value: org.name || "",
      label: org.name || "",
      id: org.id?.toString() || ""
    }));

  const handleSelect = (value: string, option: any) => {
    const org = organizations.find(org => org.name === value);
    if (org) {
      setSelectedOrg({ id: org.id?.toString() || "", name: org.name || "" });
      setSearchValue(org.name || "");
    }
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    if (!value) {
      setSelectedOrg(null);
    }
  };

  const onFinish = async (values: any) => {
    try {
      setLoading(true);
      const userData = {
        firstName: values.name,
        lastName: values.surname,
        email: values.email,
        password: values.password,
        title: values.title,
        role: getRoleValue(values.role),
        organizationName: selectedOrg?.name,
        sendPasswordWithEmail: sendPasswordWithEmail
      } as any;
      const response = await createUser(userData);
      if (response.success) {
        message.success("User created successfully");
        form.resetFields();
        setSelectedOrg(null);
        setSearchValue("");
        setOrganizations([]);
        setSendPasswordWithEmail(true);
        setLoading(false);
      } else {
        message.error(response.message || "An error occurred while creating the user");
      }
    } catch (error) {
      message.error("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ConfigProvider
      getPopupContainer={(triggerNode) => triggerNode?.parentNode as HTMLElement}
    >
      <MainLayout title="Add New User" icon={<UserOutlined />}>
        <div className={styles.formContainer}>
          <Form 
            form={form}
            layout="vertical"
            onFinish={onFinish}
          >
            <Row gutter={16}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item 
                  label="Name" 
                  name="name"
                  rules={[{ required: true, message: 'Please enter the name' }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item 
                  label="Surname" 
                  name="surname"
                  rules={[{ required: true, message: 'Please enter the surname' }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item 
                  label="Email" 
                  name="email"
                  rules={[
                    { required: true, message: 'Please enter the email' },
                    { type: 'email', message: 'Please enter a valid email address' }
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item 
                  label="Password" 
                  name="password"
                  rules={[
                    { required: true, message: 'Please enter the password' },
                    { 
                      pattern: /^(?=.*[A-Z])(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/,
                      message: 'Password must be at least 8 characters, contain at least 1 uppercase letter and 1 special character'
                    }
                  ]}
                >
                  <Input.Password />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item 
                  label="Title" 
                  name="title"
                  rules={[{ required: true, message: 'Please enter the title' }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item 
                  label="Role" 
                  name="role"
                  rules={[{ required: true, message: 'Please select a role' }]}
                >
                  <Select
                    className={styles.select}
                    options={Object.entries(RevisdomAPIViewModelsUserCreateUserRequestRoleEnum).map(([key, value]) => ({
                      label: value,
                      value: value
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item 
                  label="Organization" 
                  name="organization"
                  rules={[{ required: true, message: 'Please select an organization' }]}
                >
                  <AutoComplete
                    ref={autoCompleteRef}
                    options={options}
                    onSearch={handleSearch}
                    onSelect={handleSelect}
                    value={selectedOrg ? selectedOrg.name : searchValue}
                    style={{ width: '100%' }}
                    placeholder="Search organization..."
                    notFoundContent={loading ? "Loading..." : "No organization found"}
                    getPopupContainer={(triggerNode) => triggerNode?.parentNode as HTMLElement}
                  >
                    <Input
                      placeholder="Search organization..."
                      allowClear
                    />
                  </AutoComplete>
                </Form.Item>
              </Col>
            </Row>
            <Row justify="end" align="middle" style={{ marginTop: 24 }}>
              <Col className={styles.buttonContainer} style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <Checkbox
                  checked={sendPasswordWithEmail}
                  onChange={(e) => setSendPasswordWithEmail(e.target.checked)}
                >
                  Send user information with Email
                </Checkbox>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Create User
                </Button>
              </Col>
            </Row>
          </Form>
        </div>
      </MainLayout>
    </ConfigProvider>
  );
};

export default AddNewUser;
